import { formatSec } from "@/components/game/common";

const STATUS = { correct: ["Doğru", "text-[#6EE7B7]"], wrong: ["Yanlış", "text-red-400"], unanswered: ["Yanıtsız", "text-slate-400"], excluded: ["Bağlantı yok (dahil değil)", "text-slate-500"] };

const nameOf = (state, sid) => {
  const s = state.students.find((x) => x.id === sid);
  return s ? `${s.first_name} ${s.last_name}` : "(ayrıldı)";
};

const answerCell = (a, q) => (a.text != null && a.guesses == null ? [a.text, a.correct] : a.guesses != null ? [a.correct ? `✓ ${a.rank + 1}. (${a.guesses} tahmin)` : `${a.guesses} tahmin`, a.correct] : a.mapping ? [`${a.correct_n}/${a.total}`, a.correct_n === a.total] : [a.option_id, a.option_id === q.correct_option_id]);

const LiveAnswers = ({ state }) => {
  const q = state.question;
  const answered = Object.entries(state.answers).sort((a, b) => a[1].elapsed_ms - b[1].elapsed_ms);
  const waiting = state.students.filter((s) => !state.answers[s.id]);
  return (
    <div className="space-y-4" data-testid="teacher-live-answers">
      <h3 className="font-display text-xl font-bold">Canlı yanıtlar · {answered.length}/{state.students.length}</h3>
      {q.options.length > 0 && <div className="grid sm:grid-cols-2 gap-2">
        {q.options.map((o) => (
          <div key={o.id} className={`rounded-lg border px-3 py-2 ${o.id === q.correct_option_id ? "border-[#34D399]/60" : "border-[rgba(71,85,105,0.45)]"}`}>
            <span className="font-bold uppercase mr-2">{o.id}</span>{o.text}
            <span className="float-right font-mono-x">{answered.filter(([, a]) => a.option_id === o.id).length}</span>
          </div>
        ))}
      </div>}
      <table className="w-full text-sm">
        <tbody>
          {answered.map(([sid, a], i) => (
            <tr key={sid} className="border-t border-[rgba(71,85,105,0.45)]">
              <td className="py-1.5 text-slate-500 w-8">{i + 1}</td>
              <td className="py-1.5 font-semibold">{nameOf(state, sid)}</td>
              <td className={`py-1.5 uppercase font-bold ${answerCell(a, q)[1] ? "text-[#6EE7B7]" : "text-red-400"}`}>{answerCell(a, q)[0]}</td>
              <td className="py-1.5 text-right font-mono-x">{(a.elapsed_ms / 1000).toFixed(1)} sn</td>
            </tr>
          ))}
        </tbody>
      </table>
      {waiting.length > 0 && <p className="text-sm text-slate-400">Bekleniyor: {waiting.map((s) => `${s.first_name} ${s.last_name}`).join(", ")}</p>}
    </div>
  );
};

const ResultDetail = ({ state }) => {
  const r = state.results;
  if (r.skipped) return <p className="font-display text-2xl font-bold text-slate-300">Soru atlandı (puansız).</p>;
  const rows = Object.entries(r.per_student);
  return (
    <div className="space-y-4" data-testid="teacher-results">
      <h3 className="font-display text-xl font-bold">Sonuç · {r.correct}/{r.participants} doğru</h3>
      {r.items && (
        <ul className="rounded-lg border border-[rgba(71,85,105,0.45)] p-3 space-y-1 text-sm" data-testid="teacher-structured-items">
          {r.items.map((it) => <li key={it.id}><b>{it.label}</b> → {it.correct_label} <span className="text-slate-400">· {it.count} öğrenci doğru (%{it.pct})</span></li>)}
        </ul>
      )}
      {r.misconceptions.length > 0 && (
        <ul className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 space-y-1" data-testid="teacher-misconceptions">
          {r.misconceptions.map((m) => <li key={m.option_id}><b>{m.count}</b> öğrenci <b>"{m.text}"</b> seçti{m.label ? `: ${m.label}` : ""}</li>)}
        </ul>
      )}
      <table className="w-full text-sm">
        <thead><tr className="eyebrow text-left"><th className="py-1">Öğrenci</th><th>Yanıt</th><th>Süre</th><th>Durum</th><th className="text-right">Puan</th></tr></thead>
        <tbody>
          {rows.map(([sid, p]) => (
            <tr key={sid} className="border-t border-[rgba(71,85,105,0.45)]">
              <td className="py-1.5 font-semibold">{nameOf(state, sid)}</td>
              <td className="uppercase">{p.text != null ? p.text : p.guesses != null ? `${p.guesses} tahmin` : p.total ? `${p.correct_n}/${p.total}` : p.option_id || "—"}</td>
              <td className="font-mono-x">{p.elapsed_ms != null ? `${(p.elapsed_ms / 1000).toFixed(1)} sn` : "—"}</td>
              <td className={STATUS[p.status][1]}>{STATUS[p.status][0]}{p.streak >= 3 ? " 🔥" : ""}</td>
              <td className="text-right font-mono-x">{p.score ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Ranking = ({ state }) => (
  <div className="space-y-3" data-testid="teacher-ranking">
    <h3 className="font-display text-xl font-bold">Bireysel sıralama</h3>
    <table className="w-full text-sm">
      <thead><tr className="eyebrow text-left"><th className="py-1">#</th><th>Öğrenci</th><th>Puan</th><th>Doğru</th><th>Yanlış</th><th>Yanıtsız</th><th>Doğruluk</th><th>Ort. süre</th></tr></thead>
      <tbody>
        {state.ranking.map((r) => (
          <tr key={r.student_id} className="border-t border-[rgba(71,85,105,0.45)]">
            <td className="py-1.5">{r.rank}</td>
            <td className="font-semibold">{nameOf(state, r.student_id)}{r.streak >= 3 ? " 🔥" : ""}</td>
            <td className="font-mono-x text-[#FACC15]">{r.score}</td>
            <td>{r.correct}</td><td>{r.wrong}</td><td>{r.unanswered}</td>
            <td>%{r.accuracy}</td>
            <td className="font-mono-x">{r.avg_ms != null ? `${formatSec(r.avg_ms)} sn` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Categories = ({ state }) => {
  const r = state.ranking;
  const cats = [
    ["En yüksek puan", r.slice(0, 3), (x) => `${x.score} puan`],
    ["En çok doğru (oran)", [...r].sort((a, b) => b.accuracy - a.accuracy || (a.avg_ms ?? 1e12) - (b.avg_ms ?? 1e12)).slice(0, 3), (x) => `%${x.accuracy}`],
    ["En hızlı (doğru yanıtlar)", r.filter((x) => x.avg_ms != null).sort((a, b) => a.avg_ms - b.avg_ms || b.accuracy - a.accuracy).slice(0, 3), (x) => `${(x.avg_ms / 1000).toFixed(1)} sn`],
  ];
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="teacher-categories">
      {cats.map(([title, rows, fmt]) => (
        <div key={title} className="panel p-4 space-y-2">
          <p className="eyebrow text-[#FACC15]">{title}</p>
          {rows.map((x, i) => <p key={x.student_id} className="text-sm"><b>{i + 1}.</b> {nameOf(state, x.student_id)} <span className="text-slate-400">· {fmt(x)}</span></p>)}
        </div>
      ))}
    </div>
  );
};

export const QuizTeacher = ({ state }) => (
  <div className="space-y-6">
    {state.phase === "finished" && state.ranking?.length > 0 && <Categories state={state} />}
    {state.phase === "question" && state.question && (
      <div className="panel p-5 space-y-2">
        <p className="eyebrow">Soru {state.q_index} · {state.question.points} puan</p>
        <p className="text-xl font-bold">{state.question.text}</p>
      </div>
    )}
    {state.phase === "question" && <div className="panel p-5"><LiveAnswers state={state} /></div>}
    {state.phase === "results" && state.results && <div className="panel p-5"><ResultDetail state={state} /></div>}
    {state.ranking?.length > 0 && <div className="panel p-5"><Ranking state={state} /></div>}
  </div>
);
